import { forwardRef, type ReactNode } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
  useMonkeysStatusStates,
} from '../../provider/context';
import type { BaseSystemStateKind, BaseSystemStateProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn, hasRenderableNode } from './utils';

function SystemStateIcon({ kind }: { kind: BaseSystemStateKind }) {
  const path: Record<BaseSystemStateKind, ReactNode> = {
    error: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v6" />
        <path d="M12 17h.01" />
      </>
    ),
    permission: (
      <>
        <rect x="5" y="10" width="14" height="10" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </>
    ),
    offline: (
      <>
        <path d="M5 12.5a10 10 0 0 1 14 0" />
        <path d="M8.5 16a5 5 0 0 1 7 0" />
        <path d="M12 20h.01" />
        <path d="m4 4 16 16" />
      </>
    ),
    fallback: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M9.5 9a2.5 2.5 0 1 1 4.2 1.8c-1 .8-1.7 1.2-1.7 2.7" />
        <path d="M12 17h.01" />
      </>
    ),
  };

  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-7 w-7"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
    >
      {path[kind]}
    </svg>
  );
}

export const BaseSystemState = forwardRef<HTMLDivElement, BaseSystemStateProps>(
  function BaseSystemState(
    {
      kind = 'fallback',
      icon,
      title,
      description,
      actions,
      appearance,
      className,
      classNames,
      style,
      role,
      'aria-live': ariaLive,
      ...stateProps
    },
    ref,
  ) {
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const statusStates = useMonkeysStatusStates();
    const densityClassName =
      statusStates.density === 'compact'
        ? 'min-h-0 px-3 py-3'
        : statusStates.density === 'comfortable'
          ? 'min-h-56 px-8 py-12'
          : undefined;
    const theme = resolveBaseAppearance(resolvedAppearance);
    const resolvedIcon =
      icon === undefined ? <SystemStateIcon kind={kind} /> : icon;
    const resolvedRole =
      role ?? (kind === 'error' || kind === 'offline' ? 'alert' : 'status');

    return (
      <div
        {...componentAttributes}
        {...stateProps}
        ref={ref}
        role={resolvedRole}
        aria-live={
          ariaLive ?? (resolvedRole === 'alert' ? 'assertive' : 'polite')
        }
        data-monkeys-component="system-state"
        data-slot="root"
        data-state={kind}
        data-density={statusStates.density}
        className={cn(
          theme.slots.stateRoot,
          densityClassName,
          classNames?.root,
          className,
        )}
        style={{ ...theme.styles.stateRoot, ...style }}
      >
        {hasRenderableNode(resolvedIcon) ? (
          <div
            data-slot="icon"
            data-state={kind}
            className={cn(theme.slots.stateIcon, classNames?.icon)}
            style={theme.styles.stateIcon}
          >
            {resolvedIcon}
          </div>
        ) : null}
        {hasRenderableNode(title) ? (
          <div
            data-slot="title"
            data-state={kind}
            className={cn(theme.slots.stateTitle, classNames?.title)}
          >
            {title}
          </div>
        ) : null}
        {hasRenderableNode(description) ? (
          <div
            data-slot="description"
            data-state={kind}
            className={cn(
              theme.slots.stateDescription,
              classNames?.description,
            )}
          >
            {description}
          </div>
        ) : null}
        {hasRenderableNode(actions) ? (
          <div
            data-slot="actions"
            data-state={kind}
            className={cn(theme.slots.stateActions, classNames?.actions)}
          >
            {actions}
          </div>
        ) : null}
      </div>
    );
  },
);
